/**
 * How far through a chapter's text a reader has scrolled (ADR-0058).
 *
 * Measured over the text's own box, not the document: the title above it and
 * the links below it are not the chapter, and counting them would put a reader
 * who has not started at a tenth of the way in.
 */

/** Where the text's box is, as getBoundingClientRect gives it. */
export interface TextBox {
  top: number;
  height: number;
}

/**
 * readFraction is 0 while the text's top is at the top of the screen and 1
 * once its end has reached the bottom. A text shorter than the screen is read
 * as soon as its end is in view.
 */
export function readFraction(box: TextBox, viewport: number): number {
  const travel = box.height - viewport;
  if (travel <= 0) return box.top + box.height <= viewport ? 1 : 0;
  return clamp(-box.top / travel);
}

/** readToEnd is whether the text's last line has been on screen. */
export function readToEnd(box: TextBox, viewport: number): boolean {
  return box.top + box.height <= viewport + 1;
}

/**
 * scrollTopFor is the window scroll that puts a reader back at a saved
 * fraction — readFraction's inverse. `scrollY` is the scroll the box was
 * measured at. A place to resume from, not a promise about which line shows:
 * pictures and text size move lines around between visits.
 */
export function scrollTopFor(fraction: number, box: TextBox, viewport: number, scrollY: number) {
  const travel = Math.max(0, box.height - viewport);
  return Math.max(0, Math.round(box.top + scrollY + clamp(fraction) * travel));
}

function clamp(x: number): number {
  return Math.min(1, Math.max(0, x));
}
